import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ArrowLeft, Users, Search, Loader2, BookOpen, Mail, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEnrollments = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/admin/student-enrollments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalEnrollments = students.reduce((sum, s) => sum + s.total_courses, 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-enrollments-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          className="mb-4 text-slate-600"
          onClick={() => navigate('/admin')}
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Admin
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Student Enrollments</h1>
            <p className="text-slate-500 text-sm mt-1">
              {students.length} students with {totalEnrollments} total enrollments
            </p>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-enrollments-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No enrolled students found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(student => (
              <Card key={student.id} className="overflow-hidden" data-testid={`student-card-${student.id}`}>
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleExpand(student.id)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-primary font-semibold text-sm">
                        {(student.name || student.email || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{student.name || 'No name'}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 shrink-0" />
                        {student.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="secondary" className="bg-primary/10 text-primary">
                      <BookOpen className="w-3 h-3 mr-1" />
                      {student.total_courses} course{student.total_courses !== 1 ? 's' : ''}
                    </Badge>
                    {expanded[student.id] ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>

                {expanded[student.id] && (
                  <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/50">
                    <div className="space-y-2">
                      {student.courses.map((course, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-700 truncate mr-4">{course.title}</span>
                          {course.is_free ? (
                            <Badge variant="outline" className="text-green-600 border-green-200 text-xs shrink-0">Free</Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-600 text-xs shrink-0">${course.price}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEnrollments;
